<script setup lang="ts">
import { Card } from '@metapic-apps/shared'
import type { FormInstance, FormRules } from 'element-plus'
import { reactive, ref, watch } from 'vue'

import { isValidEmail, isValidPhone } from '@/lib/helpers/utils.helpers'
import { type IdentityFormType } from '@/lib/types/identity.types'
import { BoxComponent, ButtonComponent, FlexComponent, InputComponent, TagComponent, TitleComponent } from '@/ui-library'

const props = defineProps<{ identity: IdentityFormType; isLoading: boolean; creatorId: number }>()
const emit = defineEmits(['handleSubmit', 'handleBack'])

const formData = reactive<IdentityFormType>({
  email: props.identity.email,
  phone: props.identity.phone,
})

watch(
  () => props.identity,
  (identity) => {
    formData.email = identity.email
    formData.phone = identity.phone
  },
  { immediate: true },
)

const ruleFormRef = ref<FormInstance>()

const rules = reactive<FormRules>({
  email: [
    { required: true, message: 'This field is required', trigger: 'change' },
    {
      validator: (_rule, value, callback) => {
        if (!isValidEmail(value)) {
          callback(new Error('Invalid email format'))
        } else {
          callback()
        }
      },
      trigger: 'change',
    },
  ],
  phone: [
    {
      validator: (_rule, value, callback) => {
        if (!value) {
          callback()
          return
        }

        if (!isValidPhone(value)) {
          callback(new Error('Invalid phone number'))
          return
        }

        callback()
      },
      trigger: 'change',
    },
  ],
})

const isDirty = ref(false)

const submitForm = (formEl: FormInstance | undefined) => {
  if (!formEl) {
    return
  }

  return formEl.validate((valid) => {
    if (!valid) {
      return
    }
    isDirty.value = false
    emit('handleSubmit', formData)
  })
}
</script>

<template>
  <div class="form-container">
    <el-form
      ref="ruleFormRef"
      :model="formData"
      :rules="rules"
      hide-required-asterisk
      :validate-on-rule-change="false"
      :scroll-to-error="true"
      label-position="top"
      class="campaign-form__content"
      @change="isDirty = true"
    >
      <FlexComponent direction="column" :gap="48">
        <Card :title="`User: ${creatorId ?? ''}`">
          <TagComponent type="warning" size="large">
            Before making any identity changes, verify that the request comes from the legitimate user by contacting them through an
            independent channel such as email, phone, or social media other than the one used to submit the request.
          </TagComponent>

          <el-form-item prop="email">
            <InputComponent v-model="formData.email" name="email">
              <template #header>
                <BoxComponent :mb="8">
                  <TitleComponent title="Email"> </TitleComponent>
                </BoxComponent>
              </template>
            </InputComponent>
          </el-form-item>

          <el-form-item prop="phone">
            <InputComponent v-model="formData.phone" name="phone">
              <template #header>
                <BoxComponent :mb="8">
                  <TitleComponent title="Phone number"> </TitleComponent>
                </BoxComponent>
              </template>
            </InputComponent>
          </el-form-item>

          <FlexComponent :gap="8" justify="end">
            <ButtonComponent
              :is-loading="isLoading"
              :is-disabled="isLoading"
              data-test="submitFormBtn"
              @click.prevent="() => submitForm(ruleFormRef)"
            >
              Save
            </ButtonComponent>
          </FlexComponent>
        </Card>
      </FlexComponent>
    </el-form>
  </div>
</template>

<style scoped lang="scss">
.form-container {
  max-width: 640px;
}
:deep(.el-tag) {
  display: block;
  max-width: 100%;
  white-space: normal;
  line-height: 18px;
  padding: 5px 12px;
  height: auto;
}
</style>
